import React from 'react';
import { StarIcon, ChevronLeftIcon, SearchIcon } from 'lucide-react';
interface SidebarMaterialsPanelProps {
  cabinetMaterials: any[];
  showCabinetMaterials: boolean;
  handleCabinetMaterialsBack: () => void;
  cabinetMaterialSearch: string;
  setCabinetMaterialSearch: (search: string) => void;
  filterCabinetMaterials: () => any[];
  toggleCabinetMaterialFavorite: (materialId: string, event: React.MouseEvent) => void;
  cabinetMaterialFavorites: string[];
  handleMaterialSelect: (material: any) => void;
  showOnlyCabinetFavorites: boolean;
  setShowOnlyCabinetFavorites: (show: boolean) => void;
  showOnlySelectedMaterials: boolean;
  setShowOnlySelectedMaterials: (show: boolean) => void;
}
export function SidebarMaterialsPanel({
  cabinetMaterials,
  showCabinetMaterials,
  handleCabinetMaterialsBack,
  cabinetMaterialSearch,
  setCabinetMaterialSearch,
  filterCabinetMaterials,
  toggleCabinetMaterialFavorite,
  cabinetMaterialFavorites,
  handleMaterialSelect,
  showOnlyCabinetFavorites,
  setShowOnlyCabinetFavorites,
  showOnlySelectedMaterials,
  setShowOnlySelectedMaterials
}: SidebarMaterialsPanelProps) {
  if (!showCabinetMaterials) return null;
  return <div className="flex-1 flex flex-col overflow-hidden" data-id="element-911">
      {/* Header with back button */}
      <div className="p-3 border-b border-gray-200 dark:border-darkbg-border" data-id="element-912">
        <div className="flex items-center mb-2" data-id="element-913">
          <button onClick={handleCabinetMaterialsBack} className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-darkbg-primary mr-2" data-id="element-914">
            <ChevronLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" data-id="element-915" />
          </button>
          <h3 className="text-base font-medium text-gray-700 dark:text-gray-300" data-id="element-916">
            Cabinet Materials
          </h3>
        </div>
        {/* Search bar */}
        <div className="relative" data-id="element-917">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-918">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-919" />
          </div>
          <input type="text" value={cabinetMaterialSearch} onChange={e => setCabinetMaterialSearch(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full pl-10 p-3 dark:bg-darkbg-primary dark:border-darkbg-border dark:text-white" placeholder="Search materials..." data-id="element-920" />
        </div>
      </div>
      {/* Cabinet Materials */}
      <div className="p-3 border-b border-gray-200 dark:border-darkbg-border flex items-center justify-between" data-id="element-921">
        <h3 className="font-medium text-gray-700 dark:text-gray-300" data-id="element-922">
          Cabinet Materials
        </h3>
      </div>
      {/* Materials Grid */}
      <div className="flex-1 overflow-y-auto p-3" data-id="element-923">
        <div className="mb-3" data-id="element-924"></div>
        <div className="grid grid-cols-3 gap-2" data-id="element-925">
          {filterCabinetMaterials().map(material => {
          const isFavorite = cabinetMaterialFavorites.includes(material.id);
          // Determine gradient based on material category
          let gradientStyle = 'bg-gradient-to-br from-gray-200 to-gray-400';
          if (material.category === 'Wood') {
            gradientStyle = 'bg-gradient-to-br from-amber-100 to-amber-700';
          } else if (material.category === 'Glass') {
            gradientStyle = 'bg-gradient-to-br from-blue-100 to-blue-300';
          } else if (material.category === 'Tile') {
            gradientStyle = 'bg-gradient-to-br from-gray-100 to-gray-300';
          } else if (material.category === 'Metal') {
            gradientStyle = 'bg-gradient-to-br from-gray-300 to-gray-500';
          } else if (material.category === 'Stone') {
            gradientStyle = 'bg-gradient-to-br from-gray-200 to-gray-500';
          } else if (material.category === 'Fabric') {
            gradientStyle = 'bg-gradient-to-br from-green-200 to-green-600';
          } else if (material.category === 'Plastic') {
            gradientStyle = 'bg-gradient-to-br from-blue-200 to-blue-400';
          }
          return <div key={material.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white dark:bg-darkbg-secondary dark:border-darkbg-border hover:shadow-md transition-shadow" onClick={() => handleMaterialSelect(material)} data-id="element-926">
                <div className="relative" data-id="element-927">
                  <div className="w-full h-20 flex items-center justify-center" data-id="element-928">
                    <div className="w-16 h-16 rounded-full shadow-sm" style={{
                  background: gradientStyle
                }} data-id="element-929">
                      {material.color && material.color.startsWith('bg-') && <div className={`w-full h-full rounded-full ${material.color}`} data-id="element-930"></div>}
                    </div>
                    <button className={`absolute top-2 right-2 p-1 rounded-full ${isFavorite ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500 dark:text-gray-500 dark:hover:text-yellow-500'}`} onClick={e => toggleCabinetMaterialFavorite(material.id, e)} data-id="element-931">
                      <StarIcon className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} data-id="element-932" />
                    </button>
                  </div>
                </div>
                <div className="p-2" data-id="element-933">
                  <div className="text-xs font-medium text-gray-800 dark:text-gray-200 mb-1 line-clamp-2" data-id="element-934">
                    {material.name}
                  </div>
                  <div className="flex flex-col text-xs text-gray-500 dark:text-gray-400" data-id="element-935">
                    <span data-id="element-936">{material.category}</span>
                    {material.property && <span data-id="element-937">{material.property}</span>}
                    {material.secondaryProperty && <span data-id="element-938">{material.secondaryProperty}</span>}
                    {material.brand && <div className="text-xs font-semibold text-gray-600 dark:text-gray-300" data-id="element-939">
                        {material.brand}
                      </div>}
                  </div>
                </div>
              </div>;
        })}
        </div>
        {/* Show message if no materials match the filter */}
        {filterCabinetMaterials().length === 0 && <div className="text-center py-8" data-id="element-940">
            <p className="text-base text-gray-500 dark:text-gray-400" data-id="element-941">
              No materials match your search.
            </p>
            <button className="mt-3 text-sm text-blue-600 hover:text-blue-800 dark:text-accent-blue dark:hover:text-blue-400" onClick={() => setCabinetMaterialSearch('')} data-id="element-942">
              Clear search
            </button>
          </div>}
      </div>
    </div>;
}